import { storePath } from '../../lib/storePath';
import orderProductTags from '../../lib/orderProductTags';
import HomeCategoryPicker from '../../components/HomeCategoryPicker';
import BranchPicker from '../../components/BranchPicker';
import AreaLocationIcon from '../../components/AreaLocationIcon';
import { useAuth } from '../../context/AuthContext';
import useCatalog from '../../lib/useCatalog';
import useFavorites from '../../lib/useFavorites';

import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { get, post } from "../../lib/api";
import ProductCard from "../../components/ProductCard";
import Seo from "../../components/Seo";
import loadingLogo from "../../assets/og-image.png";
import shopIcon from '../../assets/shop.png';
import loadingStyles from "../../styles/HomeLoading.module.css";
import { Heart, Pencil, Store, Utensils } from "lucide-react";

const BANNER_EDIT_BUTTON = "inline-flex items-center justify-center gap-1.5 rounded-full border border-white/60 bg-white/15 px-3 py-1.5 text-xs font-semibold text-white shadow-sm [text-shadow:0_0_2px_#000,0_0_5px_rgba(0,0,0,0.95),0_0_10px_rgba(0,0,0,0.85),0_0_16px_rgba(0,0,0,0.6)] backdrop-blur-sm transition hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

const FAVORITES_CATEGORY = "__favorites__";
const BANNER_ICON_SHADOW = { filter: 'drop-shadow(0 0 2px #000) drop-shadow(0 0 5px rgba(0,0,0,0.95)) drop-shadow(0 0 10px rgba(0,0,0,0.85)) drop-shadow(0 0 16px rgba(0,0,0,0.6))' };

const FilterCollage = ({ images, logos = false }) => {
  const count = images.length > 3 ? 4 : images.length > 1 ? 2 : 1;
  const tiles = images.slice(0, count);
  const FallbackIcon = logos ? Store : Utensils;
  return (
    <div aria-hidden="true" className={`absolute inset-0 -z-20 grid bg-gray-100 ${count === 1 ? "grid-cols-1" : count === 2 ? "grid-cols-2" : "grid-cols-2 grid-rows-2"}`}>
      {tiles.map((src, index) => (
        <div key={index} className="flex min-h-0 min-w-0 items-center justify-center overflow-hidden bg-white">
          {src ? (
            <img src={src} alt="" loading="lazy" className={`h-full w-full ${logos ? "object-contain p-1" : "object-cover"}`} />
          ) : (
            <FallbackIcon size={24} className="text-primary/60" />
          )}
        </div>
      ))}
    </div>
  );
};
const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = (searchParams.get("q") || "").trim();
  const areaBranchId = searchParams.get("area") || '';

  const siteUrl = String(
    process.env.REACT_APP_SITE_URL || window.location.origin
  ).replace(/\/$/, "");
  const { user } = useAuth();
  const [storeDiscovery, setStoreDiscovery] = useState(null);
  const [directoryMetadata, setDirectoryMetadata] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    get('/seo/areas', { signal: controller.signal }).then(({ storeDiscovery: discovery, metadata }) => {
      if (!controller.signal.aborted) setDirectoryMetadata(metadata || null);
      if (!controller.signal.aborted && discovery?.['@type'] === 'ItemList' && discovery.itemListElement?.length) setStoreDiscovery(discovery);
    }).catch(() => {});
    return () => controller.abort();
  }, []);
  const canFavorite = user?.role === 'CUSTOMER';
  const { favoriteProductIds, toggleFavorite } = useFavorites(
    canFavorite,
    user?.id || user?.userId || '',
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [cartMessage] = useState('');
  const { data, loading, waitingForArea, error, productPage, storePage, setProductPage, setStorePage } = useCatalog({
    q: searchQuery,
    vendorId: selectedVendor?.id,
    tag: selectedCategory === FAVORITES_CATEGORY ? null : selectedCategory,
    favorites: selectedCategory === FAVORITES_CATEGORY,
    identity: user?.id || user?.userId || '',
    branchId: areaBranchId || undefined,
  });
  const areaName = data?.currentBranch?.name || "";
  const invalidArea = Boolean(areaBranchId && error);
  const filteredProducts = data?.products || [];
  const vendorList = (data?.stores || []).map((vendor) => ({ ...vendor, id: vendor._id }));
  const showComingSoon = !loading && !error && !searchQuery && !selectedVendor && !selectedCategory
    && data?.storePagination?.total === 0 && data?.productPagination?.total === 0;
  const visibleVendors = vendorList;
  const vendors = Object.fromEntries([...vendorList, ...filteredProducts.map((product) => product.store)].filter(Boolean).map((vendor) => [String(vendor._id), vendor]));
  const [categoryDisplayData, setCategoryDisplayData] = useState({
    categories: [],
    productTagOrder: [],
    images: {},
  });
  useEffect(() => {
    if (!data?.categories) return;
    setCategoryDisplayData({
      categories: data.categories,
      productTagOrder: data.productTagOrder || [],
      images: Object.fromEntries((data.categoryImages || []).map(({ tag, image }) => [tag, image])),
    });
  }, [data?.categories, data?.categoryImages, data?.productTagOrder]);
  const categories = orderProductTags(data?.categories ?? categoryDisplayData.categories, data?.productTagOrder ?? categoryDisplayData.productTagOrder);
  const categoryImages = data?.categoryImages
    ? Object.fromEntries(data.categoryImages.map(({ tag, image }) => [tag, image]))
    : categoryDisplayData.images;
  useEffect(() => {
    if (selectedCategory === FAVORITES_CATEGORY && favoriteProductIds.length === 0)
      setSelectedCategory(null);
  }, [favoriteProductIds.length, selectedCategory]);
  const [exploringCategory, setExploringCategory] = useState('');
  useEffect(() => {
    const resetAreaFilters = () => {
      setSelectedVendor(null);
      setSelectedCategory(null);
      if (searchParams.has('area')) {
        const params = new URLSearchParams(searchParams);
        params.delete('area');
        setSearchParams(params, { replace: true });
      }
    };
    window.addEventListener('branch-changed', resetAreaFilters);
    return () => window.removeEventListener('branch-changed', resetAreaFilters);
  }, [searchParams, setSearchParams]);
  const hasMoreStores = Boolean(data?.storePagination?.hasNext);
  // --------------------------------------------------
  // SHOW ALL PRODUCTS
  // --------------------------------------------------
  const handleShowAllProducts = () => {
    setProductPage(1);
    setSelectedCategory(null);
    setSelectedVendor(null);

    // Reset store pagination
    setStorePage(1);

  };

  // --------------------------------------------------
  // STORE CLICK
  // --------------------------------------------------
  const handleStoreClick = (vendor) => {
    setSelectedVendor(vendor);
    setSelectedCategory(null);

    // Reset store pagination
    setStorePage(1);


  };

  // --------------------------------------------------
  // CATEGORY CLICK
  // --------------------------------------------------
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  // --------------------------------------------------
  // VISIT STORE
  // --------------------------------------------------
  const handleVisitStore = (vendorId) => {
    const vendor = vendors[vendorId];

    if (!vendor) {
      console.warn("Vendor not found:", vendorId);
      return;
    }

    setSelectedVendor({
      id: vendorId,
      ...vendor,
    });

    setSelectedCategory(null);

  };

  // --------------------------------------------------
  // LOAD MORE STORES
  // --------------------------------------------------
  const handleLoadMoreStores = () => {
    setStorePage(storePage + 1);
  };

  // --------------------------------------------------
  // ADD TO CART
  // --------------------------------------------------
  const handleAddToCart = async (cartItem) => {
    const response = await post("/cart/items", cartItem);
    if (!response.success) throw new Error(response.message || "Failed to add product to cart.");
    return response;
  };

  // --------------------------------------------------
  // CURRENT FILTER TITLE
  // --------------------------------------------------
  const currentFilterTitle = searchQuery
    ? `Search results for "${searchQuery}"`
    : selectedVendor && selectedCategory === FAVORITES_CATEGORY
    ? `${selectedVendor.storeName} • Favorites`
    : selectedCategory === FAVORITES_CATEGORY
    ? "Favorite Products"
    : selectedVendor && selectedCategory
    ? `${selectedVendor.storeName} • ${selectedCategory}`
    : selectedVendor
    ? selectedVendor.storeName
    : selectedCategory
    ? `${selectedCategory} Products`
    : "Popular Products";


  return (
    <div className="flex min-w-0 flex-col gap-3 bg-white">
      <Seo
        title={
          !searchQuery && !areaBranchId && directoryMetadata ? directoryMetadata.title :
          showComingSoon
            ? areaName ? `BigCircle in ${areaName} | Coming soon` : "BigCircle PH | Food Delivery & Restaurants"
            : searchQuery
            ? `${searchQuery} Food & Restaurants | BigCircle`
            : data?.currentBranch?.name
            ? `Food delivery in ${data.currentBranch.name} | BigCircle`
            : "BigCircle | Food Delivery & Restaurants"
        }
        description={
          !searchQuery && !areaBranchId && directoryMetadata ? directoryMetadata.description :
          showComingSoon
            ? areaName ? `BigCircle is preparing food delivery and local restaurants in ${areaName}. Browse our service areas and check back for availability.` : "BigCircle.ph is getting ready to deliver meals from local restaurants."
            : searchQuery
            ? `Find food, menus, and restaurants matching ${searchQuery} on BigCircle.`
            : data?.currentBranch?.name
            ? `Find restaurants and food delivery in ${data.currentBranch.name}. Browse menus and order meals online with BigCircle.`
            : "Find nearby restaurants, browse menus, and order food for local delivery with BigCircle."
        }
        path={
          searchQuery
            ? `/?q=${encodeURIComponent(searchQuery)}`
            : areaBranchId
            ? `/?area=${encodeURIComponent(areaBranchId)}`
            : "/"
        }
        robots={
          searchQuery || invalidArea || areaBranchId
            ? "noindex, follow"
            : "index, follow, max-image-preview:large"
        }
        structuredData={
          searchQuery || invalidArea
            ? null
            : {
                "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "WebSite",
                  name: "BigCircle",
                  url: siteUrl,
                  potentialAction: {
                    "@type": "SearchAction",
                    target: `${siteUrl}/?q={search_term_string}`,
                    "query-input": "required name=search_term_string",
                  },
                },
                {
                  "@type": "CollectionPage",
                  name: data?.currentBranch?.name
                    ? `Food near ${data.currentBranch.name}`
                    : "Food delivery and local restaurants",
                  description: data?.currentBranch?.name
                    ? `Restaurants and stores serving ${data.currentBranch.name}.`
                    : "Discover nearby restaurants, food menus, and meal delivery on BigCircle.",
                  url: `${siteUrl}${areaBranchId ? `/?area=${encodeURIComponent(areaBranchId)}` : '/'}`,
                  ...(data?.currentBranch?.name
                    ? { spatialCoverage: { "@type": "Place", name: data.currentBranch.name } }
                    : {}),
                  mainEntity: storeDiscovery || {
                    "@type": "ItemList",
                    itemListElement: vendorList
                      .filter((vendor) => vendor.slug)
                      .map((vendor, index) => ({
                        "@type": "ListItem",
                        position: index + 1,
                        item: {
                          "@type": "Thing",
                          name: vendor.storeName,
                          url: `${siteUrl}${storePath(vendor)}`,
                        },
                      })),
                  },
                },
              ],
            }
        }
      />
      {loading || waitingForArea ? (
        <main role="status" aria-live="polite" aria-busy="true" className="flex min-h-[65vh] flex-col items-center justify-center gap-3 bg-white px-4 py-12">
          <div aria-hidden="true" className="flex flex-col items-center">
            <img src={loadingLogo} alt="" width="112" height="112" className={`h-28 w-28 object-contain ${loadingStyles.logo}`} />
            <span className="mt-2 h-2 w-16 rounded-full bg-black/15 blur-sm" />
          </div>
          <p className="sr-only">
            {waitingForArea ? 'Confirm your service area to continue.' : exploringCategory ? `Exploring ${exploringCategory} at bigcircle.ph` : "Loading stores and products..."}
          </p>
        </main>
      ) : showComingSoon ? (
        <main className="relative isolate flex min-h-[65vh] items-center justify-center overflow-hidden rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/10 via-white to-amber-50 px-5 py-12 text-center sm:px-10">
          <div aria-hidden="true" className="absolute -left-20 -top-24 -z-10 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
          <div aria-hidden="true" className="absolute -bottom-24 -right-16 -z-10 h-72 w-72 rounded-full bg-amber-200/40 blur-3xl" />
          <section className="max-w-2xl">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-primary">BigCircle.ph</p>
            <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-5xl">
              {areaName ? `Food delivery in ${areaName} ? coming soon` : "Delivering your local favorites soon"}
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-gray-600 sm:text-lg">
              We’re getting your neighborhood stores ready. Check back soon to discover and order from local favorites.
            </p>
            <a
              href="https://www.facebook.com/profile.php?id=61593698674382"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <span aria-hidden="true" className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-sm font-extrabold text-primary">f</span>
              Follow us on Facebook
            </a>
            <div className="mt-9 flex items-center justify-center gap-3 text-primary/70" aria-hidden="true">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm"><Store size={19} /></span>
              <span className="h-px w-10 bg-primary/20" />
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm"><Utensils size={19} /></span>
              <span className="h-px w-10 bg-primary/20" />
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-sm font-extrabold shadow-sm">BC</span>
            </div>
          </section>
        </main>
      ) : (
        <>
      {/* ==================================================
          CART MESSAGE
      ================================================== */}
      {cartMessage && (
        <div
          className={`fixed top-4 right-4 p-3 rounded-md shadow-lg z-50 text-white ${
            cartMessage.includes("Error") || cartMessage.includes("Failed")
              ? "bg-red-500"
              : "bg-green-500"
          }`}
        >
          {cartMessage}
        </div>
      )}

      {/* ==================================================
          BANNER
      ================================================== */}
      {data?.selectedCategory ? <section className="relative isolate -mx-4 flex w-[calc(100%+2rem)] min-h-40 flex-col overflow-hidden rounded-none sm:mx-0 sm:w-full sm:rounded-2xl sm:min-h-44 lg:min-h-48">
        <img src={data.selectedCategory.banner} alt={data.selectedCategory.label} className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
        <div className="flex w-full flex-1 flex-col px-4 py-4 text-white [text-shadow:0_0_2px_#000,0_0_5px_rgba(0,0,0,0.95),0_0_10px_rgba(0,0,0,0.85),0_0_16px_rgba(0,0,0,0.6)] sm:px-5 sm:py-5 lg:px-6 lg:py-6">
          <div className="mb-6 flex items-start justify-between gap-3 text-base font-semibold sm:text-lg">
            <p className="flex min-w-0 items-center gap-2"><img src={shopIcon} alt="" className="h-5 w-5 shrink-0 object-contain" style={{ filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.6)) drop-shadow(0 0 5px rgba(255,255,255,0.3)) drop-shadow(0 0 10px rgba(255,255,255,0.1))' }} aria-hidden="true" /><span className="break-words">{data.selectedCategory.label}</span></p>
            <p className="flex min-w-0 items-center justify-end gap-2 text-right"><AreaLocationIcon className="h-5 w-5 shrink-0" style={BANNER_ICON_SHADOW} aria-hidden="true" /><span className="break-words">{data.currentBranch?.name || 'Choose area'}</span></p>
          </div>
          <h1 className="mb-3 mt-auto w-[70%] max-w-none break-words text-lg font-semibold leading-tight sm:w-full sm:max-w-3xl sm:text-2xl lg:text-3xl">{data.selectedCategory.slogan}</h1>
          <div className="flex items-center justify-start gap-3">
            <HomeCategoryPicker user={user} currentCategory={data?.currentProductCategory} onSelecting={setExploringCategory} onSelected={handleShowAllProducts} onFailed={() => setExploringCategory('')} triggerLabel={<><span>Store type</span><Pencil className="h-3.5 w-3.5" style={BANNER_ICON_SHADOW} aria-hidden="true" /></>} triggerClassName={BANNER_EDIT_BUTTON} />
            <BranchPicker linkAreas triggerLabel="Service areas" triggerIconStyle={BANNER_ICON_SHADOW} triggerClassName={BANNER_EDIT_BUTTON} />

          </div>
        </div>
      </section> : !loading && !error && <section className="rounded-2xl border bg-primary/5 p-6"><h1 className="text-2xl font-semibold">Choose your store category</h1><p className="mt-2 text-sm text-gray-600">Select one category in your profile settings to see its stores and products.</p>{user?.role === 'CUSTOMER' ? <Link to="/profile?section=settings" className="mt-4 inline-block font-semibold text-primary underline">Choose current category</Link> : <p className="mt-2 text-sm text-gray-500">Store categories will appear once configured by an administrator.</p>}</section>}


      {!loading && !error && data && !data.currentBranch && <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">Choose your area to see nearby stores and products.</p>}
      {!data?.selectedCategory && <div className="flex flex-wrap justify-end gap-3"><BranchPicker linkAreas triggerLabel="Service areas" /><HomeCategoryPicker user={user} currentCategory={data?.currentProductCategory} onSelecting={setExploringCategory} onSelected={handleShowAllProducts} onFailed={() => setExploringCategory('')} /></div>}


      <section aria-label="Product categories">
        <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain max-sm:[scrollbar-width:none] max-sm:[&::-webkit-scrollbar]:hidden" role="group" aria-label="Filter products by category">
          {[
            {
              label: selectedVendor ? `All Categories from ${selectedVendor.storeName}` : "All Categories",
              value: null,
              images: categories.map((category) => categoryImages[category]),
            },
            ...(favoriteProductIds.length > 0
              ? [{
                  label: "Your Favorites",
                  value: FAVORITES_CATEGORY,
                  image: filteredProducts.find((product) =>
                    favoriteProductIds.includes(String(product._id)) && product.images?.some(Boolean),
                  )?.images?.find(Boolean),
                }]
              : []),
            ...categories.map((category) => ({
              label: category,
              value: category,
              image: categoryImages[category],
            })),
          ].map(({ label, value, image, images }) => {
            const selected = selectedCategory === value;
            return (
              <button
                type="button"
                key={label}
                onClick={() => handleCategoryClick(value)}
                aria-pressed={selected}
                className={`relative isolate h-20 w-32 shrink-0 snap-start overflow-hidden rounded-t-lg border-b-2 bg-gray-100 text-center transition sm:h-24 sm:w-36 ${selected ? "border-green-600" : "border-transparent hover:border-green-300"}`}
              >
                {images?.length ? (
                  <FilterCollage images={images} />
                ) : image ? (
                  <img src={image} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" />
                ) : value === FAVORITES_CATEGORY ? (
                  <div className="absolute inset-0 -z-20 flex items-center justify-center text-primary/70">
                    <Heart size={28} aria-hidden="true" />
                  </div>
                ) : (
                  <div className="absolute inset-0 -z-20 flex items-center justify-center text-primary/60">
                    <Utensils size={28} aria-hidden="true" />
                  </div>
                )}
                <span className="absolute inset-0 flex items-center justify-center px-2 text-sm font-semibold text-white [text-shadow:0_0_1px_#000,0_0_3px_rgba(0,0,0,0.9),0_0_6px_rgba(0,0,0,0.6),0_0_10px_rgba(0,0,0,0.25)]">
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section aria-label="Stores" className="flex min-w-0 flex-col gap-3">
        <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain max-sm:[scrollbar-width:none] max-sm:[&::-webkit-scrollbar]:hidden" role="group" aria-label="Filter products by store">
          {[
            { vendor: null, label: "All Stores", image: null },
            ...visibleVendors.map((vendor) => ({ vendor, label: vendor.storeName || "Store", image: vendor.banner })),
          ].map(({ vendor, label, image }) => {
            const isSelected = vendor
              ? String(selectedVendor?.id) === String(vendor.id)
              : !selectedVendor;
            const labelSize = label.length > 40 ? "text-[11px]" : label.length > 24 ? "text-xs" : "text-sm";
            return (
              <div key={vendor?.id || "all-stores"} className="w-32 max-w-32 shrink-0 snap-start sm:w-36 sm:max-w-36">
              <button
                type="button"
                onClick={() => vendor ? handleStoreClick(vendor) : handleShowAllProducts()}
                aria-pressed={isSelected}
                title={label}
                className={`relative isolate flex h-20 w-32 max-w-32 shrink-0 snap-start items-center justify-center overflow-hidden rounded-t-lg border-b-2 bg-gray-100 px-2 text-center transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:h-24 sm:w-36 sm:max-w-36 ${isSelected ? "border-green-600" : "border-transparent hover:border-green-300"}`}
              >
                {!vendor && visibleVendors.length > 0 ? (
                  <FilterCollage images={visibleVendors.map((store) => store.logo)} logos />
                ) : image ? (
                  <img src={image} alt="" loading="lazy" className="absolute inset-0 -z-20 h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 -z-20 flex items-center justify-center bg-primary/10 text-primary/60">
                    <Store size={28} aria-hidden="true" />
                  </div>
                )}
                <span className={`line-clamp-2 w-full break-words font-semibold leading-snug text-white [overflow-wrap:anywhere] [text-shadow:0_0_1px_#000,0_0_3px_rgba(0,0,0,0.9),0_0_6px_rgba(0,0,0,0.6),0_0_10px_rgba(0,0,0,0.25)] ${labelSize}`}>
                  {label}
                </span>
              </button>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 empty:hidden">
          {!loading && storePage > 1 && (
            <button type="button" className="rounded-lg border px-3 py-2 text-sm" onClick={() => setStorePage(storePage - 1)}>Previous stores</button>
          )}
          {hasMoreStores && (
            <button type="button" onClick={handleLoadMoreStores} className="rounded-lg bg-primary/5 px-3 py-2 text-sm font-medium text-primary transition hover:bg-primary/10">Next stores</button>
          )}
          {vendorList.length > 0 && (
            <p className="text-xs text-gray-400">Page {storePage} of {data?.storePagination?.totalPages || 1} - {data?.storePagination?.total || 0} stores</p>
          )}
          {vendorList.length === 0 && !loading && (
            <p className="text-sm text-gray-500">No stores available.</p>
          )}
        </div>
      </section>

      <div className="min-w-0">

        {/* ==================================================
            PRODUCTS
        ================================================== */}
        <main className="min-w-0">
          <section>
            {/* PRODUCT HEADER */}
            <div
              className="
                flex
                items-start
                justify-between
                gap-3
                mb-3
              "
            >
              <div className="min-w-0">
                <h2
                  className="
                    text-xl
                    sm:text-2xl
                    font-bold
                    truncate
                  "
                  title={currentFilterTitle}
                >
                  {currentFilterTitle}
                </h2>

                {searchQuery && !loading && (
                  <p className="text-sm text-gray-500 mt-1">
                    {filteredProducts.length}{" "}
                    {filteredProducts.length === 1 ? "product" : "products"}{" "}
                    matched
                  </p>
                )}

                {selectedVendor && !selectedCategory && (
                  <p className="text-sm text-gray-500 mt-1">
                    All products from this store
                  </p>
                )}

                {selectedVendor && selectedCategory && (
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedCategory} products from {selectedVendor.storeName}
                  </p>
                )}

                {selectedCategory && !selectedVendor && (
                  <p className="text-sm text-gray-500 mt-1">
                    Products in this category
                  </p>
                )}
              </div>

              {(searchQuery || selectedCategory || selectedVendor) &&
                !loading && (
                  <button
                    type="button"
                    onClick={() => {
                      if (searchQuery) setSearchParams({});
                      handleShowAllProducts();
                    }}
                    className="
                      flex-shrink-0
                      text-sm
                      text-primary
                      hover:underline
                    "
                  >
                    {searchQuery ? "Clear search" : "Show All"}
                  </button>
                )}
            </div>

            {/* ERROR */}
            {error && !loading && (
              <div
                className="
                  text-center
                  text-red-500
                  py-12
                  px-4
                  bg-red-50
                  rounded-xl
                "
              >
                {error}
              </div>
            )}

            {/* EMPTY */}
            {!loading && !error && filteredProducts.length === 0 && (
              <div
                className="
                    text-center
                    text-gray-500
                    py-16
                    px-4
                    bg-gray-50
                    rounded-xl
                  "
              >
                <div className="text-4xl mb-3">🍽️</div>

                <p className="font-medium">
                  {searchQuery
                    ? `No products match "${searchQuery}".`
                    : selectedVendor && selectedCategory
                    ? `No products found in "${selectedCategory}" from "${selectedVendor.storeName}".`
                    : selectedVendor
                    ? `No products found in "${selectedVendor.storeName}".`
                    : selectedCategory
                    ? `No products found in "${selectedCategory}".`
                    : "No products available."}
                </p>

                {(searchQuery || selectedVendor || selectedCategory) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (searchQuery) setSearchParams({});
                      handleShowAllProducts();
                    }}
                    className="
                        mt-3
                        text-sm
                        text-primary
                        hover:underline
                      "
                  >
                    {searchQuery ? "Clear search" : "Browse all products"}
                  </button>
                )}
              </div>
            )}

            {/* PRODUCTS */}
            {!loading && !error && filteredProducts.length > 0 && (
              <div
                className="
                    grid
                    grid-cols-2
                    sm:grid-cols-2
                    xl:grid-cols-3
                    2xl:grid-cols-4
                    gap-3
                  "
              >
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    vendor={vendors[product.vendorId]}
                    onAddToCart={handleAddToCart}
                    onVisitStore={handleVisitStore}
                    isLoading={loading}
                    canFavorite={canFavorite}
                    isFavorite={favoriteProductIds.includes(String(product._id))}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </div>
            )}
            {!loading && !error && data?.productPagination?.total > 0 && <nav aria-label="Product pagination" className="mt-3 flex items-center justify-center gap-3"><button type="button" disabled={productPage === 1} className="rounded-lg border px-4 py-2 disabled:opacity-40" onClick={() => setProductPage(productPage - 1)}>Previous</button><span className="text-sm">Page {productPage} of {data.productPagination.totalPages}</span><button type="button" disabled={!data.productPagination.hasNext} className="rounded-lg border px-4 py-2 disabled:opacity-40" onClick={() => setProductPage(productPage + 1)}>Next</button></nav>}
          </section>
        </main>
      </div>
        </>
      )}
    </div>
  );
};

export default Home;
